/* eslint-disable no-unused-vars */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import {
  Box,
  Button,
  ButtonGroup,
  Card,
  CircularProgress,
  Divider,
  Grid,
  InputAdornment,
  Menu,
  MenuItem,
  Paper,
  Popper,
  SvgIcon,
  TextField,
  Typography,
  makeStyles
} from '@material-ui/core';
import { CustomInputEvent } from 'src/types';
import {
  getCustomersQueryBuilder,
  quickFilterBranchesChoices,
  taptapCustomerInitialPayload,
  updateColumnVisibilityUtil,
  taptapOptionsCouriers,
  findSelectedQuickFilterViaParamV2,
  getTapTapQuickViewFiltersViaPermissionV2,
  quickFilterViewsLabels,
  getTaptapOperationDashboardAccessibleColumns,
  taptapOperationDashboardColumns
} from 'src/redux/slices/taptap-customer';
import {
  TapTapColumn,
  TapTapQuickFilterView,
  TapTapGetCustomersCondition,
  GetTapTapCustomersPayload,
  TapTapOperationDashboardColumn,
  TapTapCustomersSort
} from 'src/redux/slices/taptap-customer/types';
import { slices, useAppDispatch, useAppSelector } from 'src/redux';
import { cloneDeep, concat, isEqual, set } from 'lodash';
import { initialTaptapTableColumns } from 'src/redux/slices/taptap-customer/constants/taptap-table-columns';
import { CheckBoxLabel, DatePickerRangeComponent } from 'src/components';
import {
  convertToLocalISO,
  dateToday,
  getDateRangePreset,
  getBranchColor,
  snakeCaseToTitleCase,
  downloadStringCSV
} from 'src/utils';
import SearchIcon from '@material-ui/icons/Search';
import { useNavigate } from 'react-router';
import { taptapCustomerField } from 'src/redux/slices/taptap-customer/constants/taptap-fields';
import FilterListIcon from '@material-ui/icons/FilterList';
import { usePermissions, useSnackBar } from 'src/hooks';
import useResolution from 'src/hooks/useResolution';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import GetAppIcon from '@material-ui/icons/GetApp';
import ViewColumnIcon from '@material-ui/icons/ViewColumn';
import { useBranchInfo } from 'src/hooks/branch/use-branch-info';
import useUserInfo from 'src/hooks/user/use-user-info';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import AddIcon from '@material-ui/icons/Add';
import AccessTimeIcon from '@material-ui/icons/AccessTime';

interface Props {
  className?: string;
  input?: string;
  isLoading?: boolean;
  statusFilter?: string;
  courierFilter?: string;
  hasCustomers?: boolean;
  filterViewCheck: (text: string | null) => void;
  selectedDashboardView?: string | null;
  onSearchCustomer?: (text: string) => void;
  onToolbarInputChange?: (e: CustomInputEvent) => void;
  setStatusFilter?: React.Dispatch<React.SetStateAction<string>>;
  setCourierFilter?: React.Dispatch<React.SetStateAction<string>>;
}

const { selectors: userSelector } = slices.user;

const useStyles = makeStyles((theme) => ({
  root: {
    margin: theme.spacing(2)
  },
  importButton: {
    marginRight: theme.spacing(1)
  },
  exportButton: {
    marginRight: theme.spacing(1)
  },
  presetButtonGroup: {
    [theme.breakpoints.down('md')]: {
      display: 'none'
    }
  },
  dateFilterButton: {
    [theme.breakpoints.up('lg')]: {
      display: 'none'
    }
  },
  popoverPresetButtons: {
    [theme.breakpoints.up('lg')]: {
      display: 'none'
    }
  },
  toolbarDateRange: {
    width: '100%',
    '& > div': {
      width: '100%'
    },
    '& > div > div': {
      width: '100%'
    },
    '& .MuiFormControl-root': {
      minWidth: 0,
      flex: 1
    },
    '& .MuiInputBase-root': {
      height: 40
    },
    '& .MuiInputBase-input': {
      paddingTop: 10,
      paddingBottom: 10
    },
    '& .MuiInputLabel-outlined': {
      transform: 'translate(14px, 12px) scale(1)'
    },
    '& .MuiInputLabel-outlined.MuiInputLabel-shrink': {
      transform: 'translate(14px, -6px) scale(0.75)'
    }
  }
}));

const {
  actions: taptapCustomerActions,
  selectors: taptapCustomerSelectors
} = slices.taptapCustomer;

const createdAtSortOptions: Array<{ label: string; value: 'ASC' | 'DESC' }> = [
  {
    label: 'Oldest to latest',
    value: 'ASC'
  },
  {
    label: 'Latest to oldest',
    value: 'DESC'
  }
];

const defaultCreatedAtSort: TapTapCustomersSort[] = [
  {
    field: taptapCustomerField.CREATED_AT,
    direction: 'ASC'
  }
];

type QuickFilterDatePreset = 'today' | 'yesterday' | 'month';

const QUICK_FILTER_MIN_CREATED_AT_DATE = '2026-04-01';

const quickFilterDatePresets: Array<{
  label: string;
  value: QuickFilterDatePreset;
}> = [
  {
    label: 'Today',
    value: 'today'
  },
  {
    label: 'Yesterday',
    value: 'yesterday'
  },
  {
    label: 'This month',
    value: 'month'
  }
];

const salesTransferRequestPermissions = [
  'can_view_taptap_warehouse_dashboard',
  'can_view_taptap_sales_dashboard',
  'can_view_all_taptap_dashboard'
];

const isCreatedAtDateCondition = (condition: TapTapGetCustomersCondition) =>
  condition.field === taptapCustomerField.CREATED_AT &&
  (condition.operator === 'gte' || condition.operator === 'lte');

const getCreatedAtDateConditions = (
  preset: QuickFilterDatePreset
): TapTapGetCustomersCondition[] => {
  const range = getDateRangePreset(preset);

  if (!range) {
    return [];
  }

  return [
    {
      field: taptapCustomerField.CREATED_AT,
      operator: 'gte',
      value: convertToLocalISO(range.start)
    },
    {
      field: taptapCustomerField.CREATED_AT,
      operator: 'lte',
      value: convertToLocalISO(range.end, true)
    }
  ];
};

const getLocalDateInputValue = (value?: string | null) => {
  if (!value) {
    return null;
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
    2,
    '0'
  )}-${String(date.getDate()).padStart(2, '0')}`;
};

const getQuickFilterDateRangeValues = (payload: GetTapTapCustomersPayload) => {
  const dateConditions = payload?.filters?.conditions || [];

  return {
    from: getLocalDateInputValue(
      dateConditions.find(
        (condition) =>
          condition.field === taptapCustomerField.CREATED_AT &&
          condition.operator === 'gte'
      )?.value
    ),
    to: getLocalDateInputValue(
      dateConditions.find(
        (condition) =>
          condition.field === taptapCustomerField.CREATED_AT &&
          condition.operator === 'lte'
      )?.value
    )
  };
};

const addMonths = (value: string, months: number) => {
  const date = new Date(value);
  date.setMonth(date.getMonth() + months);
  return getLocalDateInputValue(date.toISOString());
};

const getQuickFilterDateRangeToMax = (fromDate?: string | null) => {
  const today = dateToday('yyyy-MM-dd');

  if (!fromDate) {
    return today;
  }

  const threeMonthsFromStart = addMonths(fromDate, 3);

  if (!threeMonthsFromStart) {
    return today;
  }

  return threeMonthsFromStart > today ? today : threeMonthsFromStart;
};

const findSelectedDatePreset = (payload: GetTapTapCustomersPayload) => {
  const dateConditions = (payload?.filters?.conditions || []).filter(
    isCreatedAtDateCondition
  );

  return (
    quickFilterDatePresets.find((preset) =>
      isEqual(dateConditions, getCreatedAtDateConditions(preset.value))
    )?.value || null
  );
};

const getOperationDashboardColumnColor = (key: string) =>
  taptapOperationDashboardColumns.find((column) => column.key === key)
    ?.bgColor || '#757575';

const operationDashboardColorLegends = [
  {
    label: 'Sales / Cashier Department',
    color: getOperationDashboardColumnColor('for_payment')
  },
  {
    label: 'Accounting / Ecomm Coordinator',
    color: getOperationDashboardColumnColor('online_fund_transfer')
  },
  {
    label: 'Warehouse Department',
    color: getOperationDashboardColumnColor('done_payment')
  },
  {
    label: 'Technical Department',
    color: getOperationDashboardColumnColor('on_queue')
  }
];

const Toolbar = ({
  className,
  hasCustomers = false,
  isLoading = false,
  statusFilter,
  setStatusFilter,
  courierFilter,
  setCourierFilter,
  filterViewCheck,
  selectedDashboardView,
  ...rest
}: Props) => {
  const navigate = useNavigate();
  const snackBar = useSnackBar();

  const classes = useStyles();
  const dispatch = useAppDispatch();
  const { xs, xl, width } = useResolution();
  const {
    canDownloadTaptap,
    canViewTaptapSalesDashboard,
    canViewTaptapAccountingDashboard,
    canViewAllTaptapDashboard,
    readOnlyTaptapAdminDashboard,
    cannotSelectTaptapBranch,
    userPermissionsStringArray
  } = usePermissions();

  const { defaultBranch } = useBranchInfo();

  const selectedSearchValue = useAppSelector(
    taptapCustomerSelectors.selectTaptapCustomerSearchValue
  );
  const selectedFilter = useAppSelector(
    taptapCustomerSelectors.selectTaptapCustomerFilter
  );
  const selectedGetPayload = useAppSelector(
    taptapCustomerSelectors.selectTaptapCustomerPayload
  );
  const selectedColumns = useAppSelector(
    taptapCustomerSelectors.selectTaptapCustomerColumns
  );
  const selectedOperationDashboardColumns = useAppSelector(
    taptapCustomerSelectors.selectTaptapOperationDashboardColumns
  );
  const taptapCustomerPayload = useAppSelector(
    taptapCustomerSelectors.selectTaptapCustomerPayload
  );

  const { userBranchNicknames } = useUserInfo();

  const [anchorEl, setAnchorEl] = useState(null);
  const [anchorDatePopper, setAnchorDatePopper] = useState(null);
  const [anchorCustomDatePopper, setAnchorCustomDatePopper] = useState<any>(
    null
  );
  const [anchorElColumns, setAnchorElColumns] = useState(null);
  const [
    anchorElOperationDashboardColumns,
    setAnchorElOperationDashboardColumns
  ] = useState(null);
  const [isExportLoading, setIsExportLoading] = useState(false);

  const [isFilterPopEverOpen, setIsFilterPopEverOpen] = useState<boolean>(
    false
  );
  const [tempPopOverGetPayload, setTempPopOverGetPayload] = useState<
    GetTapTapCustomersPayload
  >(taptapCustomerInitialPayload);

  const [isExportPopEverOpen, setIsExportPopEverOpen] = useState<boolean>(
    false
  );

  const [isColumnsPopOverOpen, setIsColumnsPopOverOpen] = useState<boolean>(
    false
  );
  const [
    isOperationDashboardColumnsPopOverOpen,
    setIsOperationDashboardColumnsPopOverOpen
  ] = useState<boolean>(false);
  const [tempPopOverColumns, setTempPopOverColumns] = useState<TapTapColumn[]>(
    []
  );
  const [
    tempOperationDashboardColumns,
    setTempOperationDashboardColumns
  ] = useState<TapTapOperationDashboardColumn[]>([]);
  const [anchorAddMenu, setAnchorAddMenu] = useState(null);

  const [dateFrom, setDateFrom] = useState<string | null>(null);
  const [dateTo, setDateTo] = useState<string | null>(null);
  const [selectedView, setSelectedView] = useState<string | null>(null);
  const [tempCourierFilter, setTempCourierFilter] = useState<string>('');

  const permissionBasedQuickFilterViews = useMemo(
    () => getTapTapQuickViewFiltersViaPermissionV2(userPermissionsStringArray),
    [userPermissionsStringArray]
  );

  const visibleQuickFilterViews = permissionBasedQuickFilterViews;
  const canViewAllBranches =
    canViewAllTaptapDashboard || readOnlyTaptapAdminDashboard;
  const selectedOperationDashboardQuickView = useMemo(() => {
    const selectedQuickFilter = findSelectedQuickFilterViaParamV2(
      selectedFilter,
      selectedGetPayload.sort
    );

    return selectedDashboardView || selectedQuickFilter?.label;
  }, [selectedDashboardView, selectedFilter, selectedGetPayload.sort]);

  const accessibleOperationDashboardColumns = useMemo(
    () =>
      getTaptapOperationDashboardAccessibleColumns(
        selectedOperationDashboardColumns,
        userPermissionsStringArray,
        selectedOperationDashboardQuickView
      ),
    [
      selectedOperationDashboardColumns,
      selectedOperationDashboardQuickView,
      userPermissionsStringArray
    ]
  );

  const selectedCreatedAtSortDirection = useMemo(() => {
    return (
      selectedGetPayload.sort?.find(
        (sort) => sort.field === taptapCustomerField.CREATED_AT
      )?.direction || 'ASC'
    );
  }, [selectedGetPayload.sort]);

  const appliedDatePreset = useMemo(
    () => findSelectedDatePreset(selectedGetPayload),
    [selectedGetPayload]
  );

  const hasCreatedAtDateConditions = useMemo(
    () =>
      (selectedGetPayload?.filters?.conditions || []).some(
        isCreatedAtDateCondition
      ),
    [selectedGetPayload]
  );

  const isCustomDateRangeSelected =
    hasCreatedAtDateConditions && !appliedDatePreset;

  const selectedToolbarDatePreset = useMemo(
    () => appliedDatePreset || (isCustomDateRangeSelected ? 'custom' : 'today'),
    [appliedDatePreset, isCustomDateRangeSelected]
  );

  const toolbarDateRange = useMemo(
    () => getQuickFilterDateRangeValues(selectedGetPayload),
    [selectedGetPayload]
  );

  const toolbarDateRangeToMax = useMemo(
    () => getQuickFilterDateRangeToMax(toolbarDateRange.from),
    [toolbarDateRange.from]
  );

  useEffect(() => {
    if (hasCreatedAtDateConditions) {
      return;
    }

    const clonedPayload = cloneDeep(selectedGetPayload);

    clonedPayload.pagination.page = 1;
    clonedPayload.filters.conditions = clonedPayload.filters.conditions.filter(
      (condition) => !isCreatedAtDateCondition(condition)
    );
    clonedPayload.filters.conditions.push(
      ...getCreatedAtDateConditions('today')
    );

    dispatch(
      taptapCustomerActions.updateTaptapCustomerGetPayload(clonedPayload)
    );
    dispatch(taptapCustomerActions.taptapFetchCustomersThunk());
  }, [dispatch, hasCreatedAtDateConditions, selectedGetPayload]);

  const onResetAllTapTapState = () => {
    dispatch(taptapCustomerActions.resetAll());
  };

  const onExportToCSV = useCallback(async () => {
    setIsExportLoading(true);
    const csvPayload = cloneDeep(taptapCustomerPayload);
    const pageSize = 1000;
    csvPayload.pagination.pageSize = pageSize;

    let taptapData: string[] = [];

    csvPayload.filters = {
      ...csvPayload.filters,
      conditions: csvPayload.filters.conditions.concat([
        ...(dateFrom
          ? [{ field: 'created_at', operator: 'gte', value: dateFrom }]
          : []),
        ...(dateFrom && dateTo
          ? [{ field: 'created_at', operator: 'lte', value: dateTo }]
          : [])
      ])
    };

    for (let currentPage = 1; ; currentPage++) {
      csvPayload.pagination.page = currentPage;

      const { data, error } = await getCustomersQueryBuilder({
        payloadArg: csvPayload
      }).csv();

      if (error) {
        snackBar.show({
          useSound: true,
          severity: 'error',
          message: error.message ?? 'Failed at getting Taptap CSV data'
        });
        setIsExportLoading(false);
        return;
      }

      if (!data || data.trim().length === 0 || !data.includes('\n')) break;

      taptapData.push(data);

      const rowCount = data.trim().split('\n').length - 1;
      if (rowCount < pageSize) {
        break;
      }
    }

    if (taptapData.length > 0) {
      const header = taptapData[0].split('\n')[0];
      const allRows = taptapData.flatMap((chunk) => {
        const lines = chunk.trim().split('\n');
        lines.shift();
        return lines;
      });

      const finalCsv = [header, ...allRows].join('\n');
      downloadStringCSV(finalCsv, 'Taptap_Customers.csv');
    } else {
      snackBar.show({
        useSound: true,
        severity: 'error',
        message: 'Failed at getting taptap csv'
      });
    }

    setIsExportLoading(false);
  }, [dateFrom, dateTo, snackBar, taptapCustomerPayload]);

  // eslint-disable-next-line no-unused-vars
  const onDownloadClick = useCallback((e) => {
    setIsExportPopEverOpen(true);
    setAnchorDatePopper(e.currentTarget);
  }, []);

  const onOperationDashboardColumnsPopOverOpen = (event: any) => {
    setTempOperationDashboardColumns(
      cloneDeep(accessibleOperationDashboardColumns)
    );
    setAnchorElOperationDashboardColumns(event.currentTarget);
    setIsOperationDashboardColumnsPopOverOpen((prev) => !prev);
  };

  const onChangeDate = useCallback(
    (date?: MaterialUiPickersDate, field?: string) => {
      if (!field) return;

      const formattedDate = date
        ? convertToLocalISO(date, field === 'to')
        : null;

      if (field === 'from') setDateFrom(formattedDate);
      if (field === 'to') setDateTo(formattedDate);
    },
    []
  );

  const onColumnsPopOverClose = () => {
    setIsColumnsPopOverOpen(false);
    setTempPopOverColumns([]); // reset state filter to initial
  };

  const onOperationDashboardColumnsPopOverClose = () => {
    setIsOperationDashboardColumnsPopOverOpen(false);
    setTempOperationDashboardColumns([]);
  };

  const onColumnsPopOverReset = () => {
    setTempPopOverColumns(initialTaptapTableColumns); // reset state filter to initial
  };

  const onOperationDashboardColumnsPopOverReset = () => {
    setTempOperationDashboardColumns(
      getTaptapOperationDashboardAccessibleColumns(
        taptapOperationDashboardColumns,
        userPermissionsStringArray,
        selectedOperationDashboardQuickView
      )
    );
  };

  const onApplyColumns = () => {
    const clonedColumns = cloneDeep(tempPopOverColumns).sort(
      (a, b) => (a.origIndex || 0) - (b.origIndex || 0)
    );
    dispatch(taptapCustomerActions.updateTaptapColumns(clonedColumns));
    onColumnsPopOverClose();
  };

  const onApplyOperationDashboardColumns = () => {
    const nextColumns = selectedOperationDashboardColumns.map((column) => {
      const updatedColumn = tempOperationDashboardColumns.find(
        (tempColumn) => tempColumn.key === column.key
      );

      return updatedColumn || column;
    });

    dispatch(
      taptapCustomerActions.updateTaptapOperationDashboardColumns(nextColumns)
    );
    onOperationDashboardColumnsPopOverClose();
  };

  const onFilterPopOverOpen = (event: any) => {
    setTempPopOverGetPayload(selectedGetPayload); // Set state filter from redux
    setSelectedView(selectedOperationDashboardQuickView || null);
    setTempCourierFilter(courierFilter || '');
    setAnchorEl(event.currentTarget);
    setIsFilterPopEverOpen((prev) => !prev);
  };

  const onFilterPopOverClose = () => {
    setIsFilterPopEverOpen(false);
    setTempPopOverGetPayload({
      ...taptapCustomerInitialPayload,
      sort: defaultCreatedAtSort
    }); // reset state filter to initial
    setTempCourierFilter('');
  };

  const onFilterPopOverClear = () => {
    setTempPopOverGetPayload({
      ...taptapCustomerInitialPayload,
      sort: defaultCreatedAtSort
    }); // reset state filter to initial
    if (setStatusFilter) {
      setStatusFilter('');
    }
    if (setCourierFilter) {
      setCourierFilter('');
    }
    setSelectedView(null);
    setTempCourierFilter('');
  };

  const onExportPopOverClear = () => {
    onChangeDate(null, 'from');
    onChangeDate(null, 'to');
  };

  const onApplyFilter = () => {
    const clonedTempPopOverPayload = cloneDeep(tempPopOverGetPayload);
    const selectedQuickFilter =
      visibleQuickFilterViews.find((view) => view.label === selectedView) ||
      findSelectedQuickFilterViaParamV2(
        clonedTempPopOverPayload.filters,
        clonedTempPopOverPayload.sort
      );

    // If quick filter is selected. Apply its default column sorting and visible
    if (
      selectedQuickFilter &&
      selectedQuickFilter.defaultColumns &&
      selectedQuickFilter.defaultColumns?.length > 0
    ) {
      const newColumnArrangement = updateColumnVisibilityUtil(
        selectedColumns,
        selectedQuickFilter.defaultColumns
      );

      dispatch(taptapCustomerActions.updateTaptapColumns(newColumnArrangement));
    } else {
      dispatch(taptapCustomerActions.resetTaptapColumns());
    }

    // IF CASHIER QUICKVIEW IS SELECTED, DO NOT ADD BRANCH_RELEASED CONDITION. DEFAULT BRANCH_RELEASED CONDITION TO ALL
    // if (
    //   selectedQuickFilter?.label === 'CASHIER' &&
    //   !canViewAllTaptapDashboard
    // ) {
    //   const clonedTempPopOverFilter = clonedTempPopOverPayload.filters;

    //   clonedTempPopOverFilter.conditions = clonedTempPopOverFilter?.conditions?.filter(
    //     (x) => x.field !== taptapCustomerField.BRANCH_RELEASED
    //   );
    //   setTempPopOverGetPayload(clonedTempPopOverPayload);
    // }

    dispatch(
      taptapCustomerActions.updateTaptapCustomerGetPayload(
        clonedTempPopOverPayload
      )
    );
    if (setCourierFilter) {
      setCourierFilter(tempCourierFilter);
    }
    dispatch(taptapCustomerActions.taptapFetchCustomersThunk());
    onFilterPopOverClose();
  };

  const onCreatedAtSortChange = (direction: string) => {
    if (direction !== 'ASC' && direction !== 'DESC') {
      return;
    }

    const clonedPayload = cloneDeep(selectedGetPayload);

    clonedPayload.pagination.page = 1;
    clonedPayload.sort = [
      {
        field: taptapCustomerField.CREATED_AT,
        direction
      }
    ];

    dispatch(
      taptapCustomerActions.updateTaptapCustomerGetPayload(clonedPayload)
    );
    dispatch(taptapCustomerActions.taptapFetchCustomersThunk());
  };

  const onToolbarDatePresetChange = (preset: string) => {
    const selectedPreset = quickFilterDatePresets.find(
      (option) => option.value === preset
    )?.value;

    if (!selectedPreset) {
      return;
    }

    const clonedPayload = cloneDeep(selectedGetPayload);
    const clonedFilter = clonedPayload.filters;

    clonedPayload.pagination.page = 1;
    clonedFilter.conditions = clonedFilter.conditions.filter(
      (condition) => !isCreatedAtDateCondition(condition)
    );
    clonedFilter.conditions.push(...getCreatedAtDateConditions(selectedPreset));

    setAnchorCustomDatePopper(null);
    dispatch(
      taptapCustomerActions.updateTaptapCustomerGetPayload(clonedPayload)
    );
    dispatch(taptapCustomerActions.taptapFetchCustomersThunk());
  };

  const selectedBranchIndicator = useMemo(() => {
    const selectedBranchCopy = selectedFilter?.conditions.find(
      (condition) => condition.field === 'branch_released'
    )?.value;

    return selectedBranchCopy || 'ALL';
  }, [selectedFilter]);

  const selectedFilterIndicator = useMemo(() => {
    return selectedOperationDashboardQuickView || 'ALL ?';
  }, [selectedOperationDashboardQuickView]);

  const isBranchSelected = useCallback(
    (params: TapTapGetCustomersCondition) => {
      if (
        tempPopOverGetPayload?.filters?.conditions &&
        tempPopOverGetPayload?.filters?.conditions?.length > 0
      ) {
        const getPayloadFilterConditions =
          tempPopOverGetPayload?.filters?.conditions;
        const fieldInConditions = getPayloadFilterConditions?.find(
          (condition) => condition.field === taptapCustomerField.BRANCH_RELEASED
        );
        if (fieldInConditions) {
          return isEqual(fieldInConditions.value, params.value);
        }
      }

      return false;
    },
    [tempPopOverGetPayload]
  );

  const onQuickFilterSelectBranch = (
    newCondition: TapTapGetCustomersCondition
  ) => {
    const clonedTempGetPayload = cloneDeep(tempPopOverGetPayload);
    const clonedTempPopOverFilter = clonedTempGetPayload?.filters;
    const index = clonedTempPopOverFilter?.conditions?.findIndex(
      (condition) => condition.field === newCondition.field
    );

    if (index !== -1) {
      // If it exists, replace the existing condition or unselect
      if (isEqual(newCondition, clonedTempPopOverFilter.conditions[index])) {
        // Pag selected na yung same quick filter, unselect
        clonedTempPopOverFilter.conditions.splice(index, 1);
      } else {
        clonedTempPopOverFilter.conditions[index] = newCondition;
      }
    } else {
      // If it doesn't exist, push the new condition
      clonedTempPopOverFilter.conditions.push(newCondition);
    }

    setTempPopOverGetPayload(clonedTempGetPayload);
  };

  const onToolbarDateRangeChange = (
    date: MaterialUiPickersDate,
    field: 'from' | 'to'
  ) => {
    const clonedPayload = cloneDeep(selectedGetPayload);
    const clonedFilter = clonedPayload.filters;
    const operator = field === 'from' ? 'gte' : 'lte';
    const currentFrom = toolbarDateRange.from;
    const maxToDate = getQuickFilterDateRangeToMax(currentFrom);
    let nextDate = date;

    if (field === 'to' && date && maxToDate && date > new Date(maxToDate)) {
      nextDate = new Date(maxToDate);
      snackBar.show({
        useSound: true,
        severity: 'error',
        message: 'Date range can only be up to 3 months.'
      });
    }

    const formattedDate = nextDate
      ? convertToLocalISO(nextDate, field === 'to')
      : null;

    clonedPayload.pagination.page = 1;
    clonedFilter.conditions = clonedFilter.conditions.filter(
      (condition) =>
        !(
          condition.field === taptapCustomerField.CREATED_AT &&
          condition.operator === operator
        )
    );

    if (formattedDate) {
      clonedFilter.conditions.push({
        field: taptapCustomerField.CREATED_AT,
        operator,
        value: formattedDate
      });
    }

    if (field === 'from') {
      const nextFrom = getLocalDateInputValue(formattedDate);
      const nextTo = getLocalDateInputValue(
        clonedFilter.conditions.find(
          (condition) =>
            condition.field === taptapCustomerField.CREATED_AT &&
            condition.operator === 'lte'
        )?.value
      );
      const nextToMax = getQuickFilterDateRangeToMax(nextFrom);

      if (nextTo && (!nextFrom || nextTo < nextFrom || nextTo > nextToMax)) {
        clonedFilter.conditions = clonedFilter.conditions.filter(
          (condition) =>
            !(
              condition.field === taptapCustomerField.CREATED_AT &&
              condition.operator === 'lte'
            )
        );
      }
    }

    dispatch(
      taptapCustomerActions.updateTaptapCustomerGetPayload(clonedPayload)
    );
    dispatch(taptapCustomerActions.taptapFetchCustomersThunk());
  };

  const isQuickViewFilterSelected = useCallback(
    (quickFilterViewParam: TapTapQuickFilterView) => {
      let clonedTempGetPayload = cloneDeep(tempPopOverGetPayload);
      let clonedTempPopOverFilter = clonedTempGetPayload?.filters;
      clonedTempPopOverFilter.conditions = clonedTempPopOverFilter.conditions.filter(
        (x) =>
          x.field !== taptapCustomerField.BRANCH_RELEASED &&
          !isCreatedAtDateCondition(x)
      );
      const isEmptyDashboardFilter =
        clonedTempPopOverFilter.conditions.length === 0 &&
        quickFilterViewParam.filters.conditions.length === 0;

      if (isEmptyDashboardFilter) {
        return selectedView === quickFilterViewParam.label;
      }

      if (isEqual(clonedTempPopOverFilter, quickFilterViewParam.filters)) {
        return true;
      }

      return false;
    },
    [selectedView, tempPopOverGetPayload]
  );

  const onQuickFilterViewSelect = (quickFilterView: TapTapQuickFilterView) => {
    // Deep clone the tempPopOverFilter to avoid mutation
    let clonedTempGetPayload = cloneDeep(tempPopOverGetPayload);
    let clonedTempPopOverFilter = clonedTempGetPayload?.filters;

    // Find the selected branch condition
    const selectedBranchCondition = clonedTempPopOverFilter?.conditions?.find(
      (condition) => condition?.field === taptapCustomerField.BRANCH_RELEASED
    );
    const selectedDateConditions = clonedTempPopOverFilter?.conditions?.filter(
      isCreatedAtDateCondition
    );

    clonedTempPopOverFilter.conditions = clonedTempPopOverFilter.conditions.filter(
      (x) =>
        x.field !== taptapCustomerField.BRANCH_RELEASED &&
        !isCreatedAtDateCondition(x)
    );
    // Deep clone quickFilterView.filters to avoid mutation
    const clonedQuickFilterView = cloneDeep(quickFilterView);
    let newFilter = clonedQuickFilterView.filters;
    let newSort = clonedQuickFilterView.sort;
    // TODO: SORTING LOGIC ADD HERE
    if (isQuickViewFilterSelected(quickFilterView)) {
      // If the quick filter is already selected, unselect it
      newFilter.conditions = [];
      newSort = defaultCreatedAtSort;
    }

    // Add back the branch condition if present
    if (selectedBranchCondition && selectedBranchCondition?.value?.length > 0) {
      newFilter.conditions.push(selectedBranchCondition);
    }
    if (selectedDateConditions?.length) {
      newFilter.conditions.push(...selectedDateConditions);
    }

    const finalTempGetPayload = {
      ...clonedTempGetPayload,
      filters: newFilter,
      sort: newSort
    };

    // Update the state with the new filter
    // Add sorting logic
    setTempPopOverGetPayload(finalTempGetPayload);
  };

  const onToolbarInputChange = (e: CustomInputEvent) => {
    dispatch(taptapCustomerActions.updateSearchValue(e.target.value));
  };

  const onSearchClick = () => {
    dispatch(taptapCustomerActions.taptapFetchCustomersThunk());
  };

  const onKeyPress = (e: any) => {
    if (e.key === 'Enter') {
      dispatch(taptapCustomerActions.updateSearchValue(e.target.value));
      dispatch(taptapCustomerActions.taptapFetchCustomersThunk());
    }
  };

  const onAddCustomerClick = () => {
    setAnchorAddMenu(null);
    navigate(`/app/customer-taptap/add`);
  };

  const onAddSalesTransferRequestClick = () => {
    setAnchorAddMenu(null);
    navigate(`/app/customer-taptap/sales-transfer-request/add`);
  };

  const canCreateSalesTransferRequest = userPermissionsStringArray.some(
    (permission: any) => salesTransferRequestPermissions.includes(permission)
  );
  const canCreateCustomer =
    canViewAllTaptapDashboard ||
    canViewTaptapSalesDashboard ||
    canViewTaptapAccountingDashboard;
  const canOpenAddMenu =
    !readOnlyTaptapAdminDashboard &&
    (canCreateCustomer || canCreateSalesTransferRequest);

  return (
    <div className={clsx(classes.root, className)} {...rest}>
      <Card>
        <Grid
          container
          style={{
            padding: '1em',
            alignItems: 'center'
          }}
          spacing={1}
        >
          <Grid item id="branch-label" xs={12} md={6} lg={6} xl={6}>
            <Typography variant="h4">
              DASHBOARD: {selectedFilterIndicator}
            </Typography>
          </Grid>

          <Grid item xs={12} md={6} lg={6} xl={6}>
            <Box
              display="flex"
              alignItems="center"
              flexWrap="wrap"
              style={{ gap: 12, marginTop: 8 }}
              justifyContent={'flex-end'}
            >
              {operationDashboardColorLegends.map((legend) => (
                <Box
                  key={legend.label}
                  display="flex"
                  alignItems="center"
                  style={{ gap: 6 }}
                >
                  <Box
                    style={{
                      width: 14,
                      height: 14,
                      borderRadius: 2,
                      backgroundColor: legend.color
                    }}
                  />
                  <Typography variant="caption">{legend.label}</Typography>
                </Box>
              ))}
            </Box>
          </Grid>

          <Grid item xs={12} md={12} xl={12}>
            <Typography variant="h5">
              <span
                style={{
                  color: getBranchColor(
                    Array.isArray(selectedBranchIndicator)
                      ? selectedBranchIndicator[0]
                      : undefined
                  )
                }}
              >
                {Array.isArray(selectedBranchIndicator)
                  ? ` CURRENT BRANCH: ${selectedBranchIndicator[0]}`
                  : null}
              </span>
            </Typography>
          </Grid>

          <Grid
            id="searchbar"
            item
            xs={12}
            container
            spacing={2}
            alignItems="center"
          >
            <Grid
              item
              style={{
                display: 'flex',
                alignItems: 'center'
              }}
              xs={12}
              sm={12}
              md={4}
              lg={3}
              xl={3}
            >
              <TextField
                disabled={isExportLoading}
                value={selectedSearchValue}
                onChange={onToolbarInputChange}
                fullWidth
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SvgIcon fontSize="small" color="action">
                        <SearchIcon />
                      </SvgIcon>
                    </InputAdornment>
                  ),
                  style: {
                    height: '40px'
                  }
                }}
                placeholder="Search First Name / Transaction No"
                variant="outlined"
                onKeyPress={onKeyPress}
              />

              <Button
                disabled={isExportLoading}
                onClick={onSearchClick}
                color="primary"
                variant="contained"
                style={{
                  height: '40px',
                  minWidth: '40px',
                  padding: 0,
                  marginLeft: 5
                }}
              >
                <SearchIcon />
              </Button>
            </Grid>

            <Grid item xs={12} sm={4} md={2} lg={2} xl={2}>
              <TextField
                select
                disabled={isExportLoading}
                label="Sort"
                value={selectedCreatedAtSortDirection}
                onChange={(event) => onCreatedAtSortChange(event.target.value)}
                fullWidth
                size="small"
                variant="outlined"
              >
                {createdAtSortOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item className={classes.presetButtonGroup} lg={3} xl={3}>
              <ButtonGroup color="primary" size="small" fullWidth>
                {quickFilterDatePresets.map((preset) => (
                  <Button
                    key={preset.value}
                    disabled={isExportLoading}
                    variant={
                      selectedToolbarDatePreset === preset.value
                        ? 'contained'
                        : 'outlined'
                    }
                    onClick={() => onToolbarDatePresetChange(preset.value)}
                    style={{ height: 40, fontSize: '0.6em' }}
                  >
                    {preset.label}
                  </Button>
                ))}
                <Button
                  disabled={isExportLoading}
                  variant={
                    selectedToolbarDatePreset === 'custom'
                      ? 'contained'
                      : 'outlined'
                  }
                  onClick={(event) =>
                    setAnchorCustomDatePopper(event.currentTarget)
                  }
                  style={{ height: 40, fontSize: '0.6em' }}
                >
                  Custom
                </Button>
              </ButtonGroup>
            </Grid>

            <Grid item xs={12} sm={8} md={6} lg={4}>
              <Box display="flex" justifyContent="flex-end">
                <Button
                  className={classes.dateFilterButton}
                  disabled={isExportLoading}
                  onClick={(event) =>
                    setAnchorCustomDatePopper(event.currentTarget)
                  }
                  color="primary"
                  variant="outlined"
                  style={{ marginRight: 15 }}
                >
                  <AccessTimeIcon />
                </Button>

                <Button
                  disabled={isExportLoading}
                  onClick={onFilterPopOverOpen}
                  color="primary"
                  variant="outlined"
                  style={{ marginRight: 15 }}
                >
                  <FilterListIcon />
                </Button>

                <Button
                  disabled={isExportLoading}
                  onClick={onOperationDashboardColumnsPopOverOpen}
                  color="primary"
                  variant="outlined"
                  style={{ marginRight: 15 }}
                >
                  <ViewColumnIcon />
                </Button>

                {canDownloadTaptap ? (
                  <Button
                    onClick={onDownloadClick}
                    disabled={!hasCustomers || isLoading || isExportLoading}
                    variant="outlined"
                    color="primary"
                    style={{ marginRight: 15 }}
                  >
                    <GetAppIcon />
                  </Button>
                ) : null}

                {canOpenAddMenu ? (
                  <Button
                    disabled={isExportLoading}
                    onClick={(event: any) =>
                      setAnchorAddMenu(event.currentTarget)
                    }
                    color="primary"
                    variant="contained"
                    startIcon={<AddIcon />}
                  >
                    Add
                  </Button>
                ) : null}
              </Box>
            </Grid>
          </Grid>
        </Grid>
      </Card>

      <Menu
        anchorEl={anchorAddMenu}
        keepMounted
        open={Boolean(anchorAddMenu)}
        onClose={() => setAnchorAddMenu(null)}
      >
        {canCreateCustomer ? (
          <MenuItem onClick={onAddCustomerClick}>Customer</MenuItem>
        ) : null}
        {canCreateSalesTransferRequest ? (
          <MenuItem onClick={onAddSalesTransferRequestClick}>
            Sales Transfer Request
          </MenuItem>
        ) : null}
      </Menu>

      <Popper
        open={Boolean(anchorCustomDatePopper)}
        anchorEl={anchorCustomDatePopper}
        placement={'bottom-start'}
        style={{ zIndex: 3 }}
      >
        <ClickAwayListener onClickAway={() => setAnchorCustomDatePopper(null)}>
          <Paper elevation={10} style={{ padding: 12 }}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              mb={1}
            >
              <Typography variant="h4">Date Filters</Typography>
            </Box>
            <Divider />
            <Box className={classes.popoverPresetButtons} mt={2} mb={2}>
              <ButtonGroup color="primary" size="small" fullWidth>
                {quickFilterDatePresets.map((preset) => (
                  <Button
                    key={preset.value}
                    disabled={isExportLoading}
                    variant={
                      selectedToolbarDatePreset === preset.value
                        ? 'contained'
                        : 'outlined'
                    }
                    onClick={() => onToolbarDatePresetChange(preset.value)}
                  >
                    {preset.label}
                  </Button>
                ))}
              </ButtonGroup>
            </Box>
            <Box className={classes.toolbarDateRange} mt={2}>
              <DatePickerRangeComponent
                fromDateMin={QUICK_FILTER_MIN_CREATED_AT_DATE}
                fromDateMax={toolbarDateRange.to || dateToday('yyyy-MM-dd')}
                fromDateValue={toolbarDateRange.from}
                toDateValue={toolbarDateRange.to}
                dateFromLabel="Custom Start Date"
                dateToLabel="Custom End Date"
                toDateMin={
                  toolbarDateRange.from || QUICK_FILTER_MIN_CREATED_AT_DATE
                }
                toDateMax={toolbarDateRangeToMax}
                onChangeFromDate={(date) =>
                  onToolbarDateRangeChange(date, 'from')
                }
                onChangeToDate={(date) => onToolbarDateRangeChange(date, 'to')}
              />
            </Box>
          </Paper>
        </ClickAwayListener>
      </Popper>

      <Popper
        open={isExportPopEverOpen}
        anchorEl={anchorDatePopper}
        placement={'bottom'}
        style={{ zIndex: 2 }}
      >
        <Paper elevation={10} style={{ padding: 12 }}>
          {isExportLoading && (
            <Box
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                backgroundColor: 'rgba(255, 255, 255, 0.7)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 5,
                borderRadius: 4 // match Paper's border radius
              }}
            >
              <CircularProgress />
            </Box>
          )}

          <div
            style={{
              display: 'flex',
              justifyContent: 'end',
              marginBottom: '.5em'
            }}
          >
            <Button
              style={{ marginLeft: 10 }}
              size="small"
              variant="text"
              color="secondary"
              onClick={onExportPopOverClear}
              disabled={isExportLoading}
            >
              Clear
            </Button>
            <Button
              style={{ marginLeft: 10 }}
              size="small"
              variant="text"
              color="primary"
              onClick={() => setIsExportPopEverOpen(false)}
              disabled={isExportLoading}
            >
              Close
            </Button>
            <Button
              size="small"
              color="primary"
              variant="contained"
              onClick={onExportToCSV}
              style={{ marginLeft: 10 }}
              disabled={isExportLoading}
            >
              Export to CSV
            </Button>
          </div>
          <Divider />
          <Box
            display="flex"
            flexDirection="row"
            alignItems="center"
            justifyContent="space-between"
            style={{ margin: '1em' }}
          >
            <DatePickerRangeComponent
              title="Created Date Range"
              fromDateMax={dateToday()}
              fromDateValue={dateFrom}
              toDateValue={dateTo}
              dateToLabel="End Date"
              dateFromLabel="Start Date"
              toDateMin={dateFrom}
              onChangeToDate={(date) => onChangeDate(date, 'to')}
              onChangeFromDate={(date) => onChangeDate(date, 'from')}
            />
          </Box>
        </Paper>
      </Popper>

      {/* TODO: Make a component */}
      <Popper
        open={isFilterPopEverOpen}
        anchorEl={anchorEl}
        placement={'bottom'}
        style={{ zIndex: 2, maxWidth: 700 }}
      >
        <ClickAwayListener onClickAway={onFilterPopOverClose}>
          <Paper elevation={10} style={{ padding: 12 }}>
            <Box
              display="flex"
              flexDirection="row"
              alignItems="center"
              justifyContent="space-between"
              mb={1}
              mr={1}
            >
              <Typography variant="h4">Quick Filters</Typography>
              <div>
                {/* <Button
                style={{ marginLeft: 10 }}
                size="small"
                variant="text"
                color="secondary"
                onClick={onFilterPopOverClear}
              >
                Clear
              </Button> */}
                <Button
                  style={{ marginLeft: 10 }}
                  size="small"
                  variant="text"
                  color="primary"
                  onClick={onFilterPopOverClose}
                >
                  Close
                </Button>
                <Button
                  size="small"
                  color="primary"
                  variant="contained"
                  onClick={() => {
                    onApplyFilter();
                    filterViewCheck(selectedView);
                  }}
                  style={{ marginLeft: 10 }}
                >
                  Apply
                </Button>
              </div>
            </Box>
            <Divider />
            {cannotSelectTaptapBranch ? null : (
              <div>
                <Typography style={{ marginTop: 20 }} variant="h5">
                  BRANCHES
                </Typography>
                <Box mt={1}>
                  {quickFilterBranchesChoices
                    ?.filter(
                      (choice) =>
                        Array.isArray(choice.value) &&
                        (canViewAllBranches ||
                          userBranchNicknames?.includes(choice.value[0]))
                    )
                    .map((choice) => {
                      const branchName = Array.isArray(choice.value)
                        ? choice.value[0]
                        : choice.value;
                      const branchColor = getBranchColor(branchName);

                      return (
                        <Button
                          key={`${choice?.field}-${branchName}`}
                          color="primary"
                          style={{
                            marginRight: 10,
                            color: branchColor
                          }}
                          onClick={() => onQuickFilterSelectBranch(choice)}
                          variant={
                            isBranchSelected(choice) ? 'contained' : 'outlined'
                          }
                        >
                          {branchName}
                        </Button>
                      );
                    })}
                </Box>
              </div>
            )}

            <Typography style={{ marginTop: 20 }} variant="h5">
              COURIERS
            </Typography>
            <Box mt={1}>
              {taptapOptionsCouriers.map((option) => (
                <Button
                  key={option.key}
                  color="primary"
                  style={{ marginRight: 10, marginBottom: 10 }}
                  onClick={() =>
                    setTempCourierFilter((prev) =>
                      prev === option.value ? '' : option.value
                    )
                  }
                  variant={
                    tempCourierFilter === option.value
                      ? 'contained'
                      : 'outlined'
                  }
                >
                  {option.label}
                </Button>
              ))}
            </Box>

            {visibleQuickFilterViews.length > 0 ? (
              <Typography style={{ marginTop: 20 }} variant="h5">
                DASHBOARDS
              </Typography>
            ) : null}

            <Box mt={1}>
              {visibleQuickFilterViews.length > 0 ? (
                <>
                  {visibleQuickFilterViews?.map((choice) => (
                    <Button
                      key={choice.id}
                      style={{ marginRight: 10, marginBottom: 10 }}
                      color="primary"
                      onClick={() => {
                        if (isQuickViewFilterSelected(choice)) return;
                        onQuickFilterViewSelect(choice);
                        setSelectedView(choice?.label);
                      }}
                      variant={
                        isQuickViewFilterSelected(choice)
                          ? 'contained'
                          : 'outlined'
                      }
                    >
                      {choice.label}
                    </Button>
                  ))}
                </>
              ) : null}
            </Box>
          </Paper>
        </ClickAwayListener>
      </Popper>

      {/* DASHBOARD COLUMN CONTROL */}
      <Popper
        open={isOperationDashboardColumnsPopOverOpen}
        anchorEl={anchorElOperationDashboardColumns}
        placement={'bottom'}
        style={{ zIndex: 9999, width: 820 }}
      >
        <ClickAwayListener
          onClickAway={onOperationDashboardColumnsPopOverClose}
        >
          <Paper elevation={10} style={{ padding: 12 }}>
            <Box
              display="flex"
              flexDirection="row"
              alignItems="center"
              justifyContent="space-between"
              mb={1}
              mr={1}
            >
              <Typography variant="h4">Dashboard Columns</Typography>
              <div>
                <Button
                  style={{ marginLeft: 10 }}
                  size="small"
                  variant="text"
                  color="primary"
                  onClick={onOperationDashboardColumnsPopOverClose}
                >
                  Close
                </Button>
                <Button
                  size="small"
                  color="primary"
                  variant="contained"
                  onClick={onApplyOperationDashboardColumns}
                  style={{ marginLeft: 10 }}
                >
                  Apply
                </Button>
              </div>
            </Box>
            <Divider />

            <Box
              mt={1}
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                maxHeight: 460,
                overflowY: 'auto'
              }}
            >
              {tempOperationDashboardColumns?.map((column) => (
                <CheckBoxLabel
                  key={column.key}
                  label={column.label}
                  checked={column.visible}
                  onChange={(value) => {
                    setTempOperationDashboardColumns((prev) => {
                      const index = prev.findIndex(
                        (item) => item.key === column.key
                      );

                      const newColumns = [...prev];
                      newColumns[index] = {
                        ...newColumns[index],
                        visible: value
                      };

                      return newColumns;
                    });
                  }}
                />
              ))}
            </Box>
          </Paper>
        </ClickAwayListener>
      </Popper>

      {/* TODO: Make a component */}
      <Popper
        open={isColumnsPopOverOpen}
        anchorEl={anchorElColumns}
        placement={'bottom'}
        style={{ zIndex: 9999, width: 500 }}
      >
        <Paper elevation={10} style={{ padding: 12 }}>
          <Box
            display="flex"
            flexDirection="row"
            alignItems="center"
            justifyContent="space-between"
            mb={1}
            mr={1}
          >
            <Typography variant="h4">Hide / Unhide Columns</Typography>
            <div>
              <Button
                style={{ marginLeft: 10 }}
                size="small"
                variant="text"
                color="secondary"
                onClick={onColumnsPopOverReset}
              >
                Clear
              </Button>
              <Button
                style={{ marginLeft: 10 }}
                size="small"
                variant="text"
                color="primary"
                onClick={onColumnsPopOverClose}
              >
                Close
              </Button>
              <Button
                size="small"
                color="primary"
                variant="contained"
                onClick={onApplyColumns}
                style={{ marginLeft: 10 }}
              >
                Apply
              </Button>
            </div>
          </Box>
          <Divider />

          <Typography style={{ marginTop: 20 }} variant="h5">
            DASHBOARDS
          </Typography>
          <Box mt={1}>
            {tempPopOverColumns?.map((column) => (
              <CheckBoxLabel
                key={column.field}
                label={snakeCaseToTitleCase(column.field)}
                checked={column.visible}
                onChange={(value) => {
                  setTempPopOverColumns((prev) => {
                    const index = prev.findIndex(
                      (item) => item.field === column.field
                    );

                    const newColumns = [...prev];
                    newColumns[index] = {
                      ...newColumns[index],
                      visible: value
                    };

                    return newColumns;
                  });
                }}
              />
            ))}
          </Box>
        </Paper>
      </Popper>
    </div>
  );
};

export default Toolbar;
